import React, { useState, useEffect, useRef } from 'react';
import { EmailDomainCard } from '@/components/billing/EmailDomainCard';
import { SavedThemePresets } from '@/components/settings/SavedThemePresets';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Building2, Upload, Trash2, Save, Palette, FileText, Globe, 
  Phone, Mail, MapPin, Hash, Instagram, Facebook, Linkedin, Twitter, Youtube,
  Image as ImageIcon, AlertCircle, Check, Loader2, IndianRupee
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { FeatureLocked } from '@/components/FeatureLocked';

// Logo URLs are served via /api/ routes (relative path)
const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  const cacheBuster = `?t=${Date.now()}`;
  return logoUrl + cacheBuster;
};

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const { refreshTheme } = useTheme();
  const [settings, setSettings] = useState({
    organization_name: '',
    legal_name: '',
    industry: 'Interior Design',
    nature_of_business: 'Interior Design',
    logo_url: null,
    email: '',
    phone: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    primary_color: '#D4A574',
    secondary_color: '#78716C',
    accent_color: '#292524',
    gstin: '',
    pan: '',
    social_facebook: '',
    social_instagram: '',
    social_linkedin: '',
    social_twitter: '',
    social_youtube: '',
    invoice_prefix: 'INV',
    quotation_prefix: 'QT',
    po_prefix: 'PO',
    default_payment_terms: '50% advance, 40% progressive, 10% on completion',
    default_warranty_terms: '',
    // Bank details
    bank_account_name: '',
    bank_name: '',
    bank_branch: '',
    bank_account_no: '',
    bank_ifsc: '',
    upi_id: '',
  });
  
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwnerRole = user?.role === 'owner';
  const canEdit = ['owner', 'staff', 'admin'].includes(user.role);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/organization/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!canEdit) {
      toast.error('You do not have permission to update settings');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/organization/settings`, settings);
      toast.success('Settings saved successfully');
      // Refresh the global theme to apply new branding colors
      refreshTheme();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (PNG, JPG, GIF, WebP)');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/organization/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!settings.logo_url) return;

    try {
      await axios.delete(`${API}/organization/logo`);
      setSettings(prev => ({ ...prev, logo_url: null }));
      toast.success('Logo deleted');
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </Layout>
    );
  }

  if (!canEdit) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-stone-900 mb-2">Access Restricted</h1>
          <p className="text-stone-600">Only Owner and Staff members can access organization settings.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Organization Settings</h1>
            <p className="text-stone-500 mt-1">Manage your organization profile and branding</p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className={`grid w-full ${isOwnerRole ? "grid-cols-8" : "grid-cols-7"} mb-6`}>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
            <TabsTrigger value="automation" data-testid="settings-tab-automation">Automation</TabsTrigger>
            <TabsTrigger value="ai-credits" data-testid="settings-tab-ai-credits">AI Credits</TabsTrigger>
            {isOwnerRole && <TabsTrigger value="email-domain" data-testid="settings-tab-email-domain">Email Domain</TabsTrigger>}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            {/* Logo Section */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-amber-600" />
                Organization Logo
              </h2>
              
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  {settings.logo_url ? (
                    <div className="relative">
                      <img
                        key={settings.logo_url}
                        src={getLogoUrl(settings.logo_url)}
                        alt="Organization Logo"
                        className="w-32 h-32 object-contain border border-stone-200 rounded-lg bg-white"
                        data-testid="org-logo"
                        onError={(e) => {
                          console.error('Logo load error:', e.target.src);
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        onClick={handleDeleteLogo}
                        className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        title="Delete logo"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 border-2 border-dashed border-stone-300 rounded-lg flex items-center justify-center bg-stone-50">
                      <Building2 className="w-12 h-12 text-stone-400" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <p className="text-sm text-stone-600 mb-3">
                    Upload your organization logo. This will appear on all exported PDFs including quotations, invoices, and BOQs.
                  </p>
                  <p className="text-xs text-stone-500 mb-4">
                    Recommended: 240 × 240 pixels, PNG or JPG, max 2MB
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    className="hidden"
                  />
                  <FeatureLocked capability="can_upload_logo" label="Custom Logo (White-label)">
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Logo
                        </>
                      )}
                    </Button>
                  </FeatureLocked>
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-600" />
                Basic Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="org_name">Organization Name *</Label>
                  <Input
                    id="org_name"
                    value={settings.organization_name}
                    onChange={(e) => handleChange('organization_name', e.target.value)}
                    placeholder="Bella Casa Interiors"
                    data-testid="org-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="legal_name">Legal Name</Label>
                  <Input
                    id="legal_name"
                    value={settings.legal_name || ''}
                    onChange={(e) => handleChange('legal_name', e.target.value)}
                    placeholder="Bella Casa Interiors Pvt. Ltd."
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Select 
                    value={settings.industry || 'Interior Design'} 
                    onValueChange={(v) => handleChange('industry', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Interior Design">Interior Design</SelectItem>
                      <SelectItem value="Architecture">Architecture</SelectItem>
                      <SelectItem value="Construction">Construction</SelectItem>
                      <SelectItem value="Real Estate">Real Estate</SelectItem>
                      <SelectItem value="Home Decor">Home Decor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* iter-386u-aj — Nature of Business drives AI persona + category presets */}
                <div>
                  <Label htmlFor="nature_of_business">Nature of Business</Label>
                  <Select
                    value={settings.nature_of_business || 'Interior Design'}
                    onValueChange={(v) => handleChange('nature_of_business', v)}
                  >
                    <SelectTrigger data-testid="nature-of-business-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Interior Design">Interior Design</SelectItem>
                      <SelectItem value="Landscaping">Landscaping</SelectItem>
                      <SelectItem value="General Construction">General Construction</SelectItem>
                      <SelectItem value="Retail Fit-out">Retail Fit-out</SelectItem>
                      <SelectItem value="Modular Kitchen">Modular Kitchen</SelectItem>
                      <SelectItem value="Facility Management">Facility Management</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] text-stone-500">This tells the AI Task Creator and T&amp;C generator which industry vocabulary to use, and pre-seeds matching BOQ categories in new projects.</p>
                  {/* iter-386u-aq — Live Persona Preview */}
                  <PersonaPreview natureName={settings.nature_of_business || 'Interior Design'} />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="website"
                      value={settings.website || ''}
                      onChange={(e) => handleChange('website', e.target.value)}
                      placeholder="https://www.example.com"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-amber-600" />
                Contact Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="email"
                      type="email"
                      value={settings.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="info@bellacasa.com"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="phone"
                      value={settings.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="+91 98765 43210"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-600" />
                Address
              </h2>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="address1">Address Line 1</Label>
                  <Input
                    id="address1"
                    value={settings.address_line1 || ''}
                    onChange={(e) => handleChange('address_line1', e.target.value)}
                    placeholder="Building name, Street address"
                  />
                </div>
                <div>
                  <Label htmlFor="address2">Address Line 2</Label>
                  <Input
                    id="address2"
                    value={settings.address_line2 || ''}
                    onChange={(e) => handleChange('address_line2', e.target.value)}
                    placeholder="Area, Landmark"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={settings.city || ''}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="Mumbai"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={settings.state || ''}
                      onChange={(e) => handleChange('state', e.target.value)}
                      placeholder="Maharashtra"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pincode">Pincode</Label>
                    <Input
                      id="pincode"
                      value={settings.pincode || ''}
                      onChange={(e) => handleChange('pincode', e.target.value)}
                      placeholder="400001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={settings.country || 'India'}
                      onChange={(e) => handleChange('country', e.target.value)}
                      placeholder="India"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Information */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <Hash className="w-5 h-5 text-amber-600" />
                Tax Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gstin">GSTIN</Label>
                  <Input
                    id="gstin"
                    value={settings.gstin || ''}
                    onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                  />
                </div>
                <div>
                  <Label htmlFor="pan">PAN</Label>
                  <Input
                    id="pan"
                    value={settings.pan || ''}
                    onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                    placeholder="AAAPA1234A"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6">
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-amber-600" />
                Brand Colors
              </h2>
              <p className="text-sm text-stone-500 mb-6">
                These colors will be used in exported documents and can be applied to the application theme.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="primary_color">Primary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      id="primary_color"
                      value={settings.primary_color || '#D4A574'}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="w-12 h-10 rounded border border-stone-200 cursor-pointer"
                    />
                    <Input
                      value={settings.primary_color || '#D4A574'}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="secondary_color">Secondary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      id="secondary_color"
                      value={settings.secondary_color || '#78716C'}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      className="w-12 h-10 rounded border border-stone-200 cursor-pointer"
                    />
                    <Input
                      value={settings.secondary_color || '#78716C'}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="accent_color">Accent Color</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      id="accent_color"
                      value={settings.accent_color || '#292524'}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      className="w-12 h-10 rounded border border-stone-200 cursor-pointer"
                    />
                    <Input
                      value={settings.accent_color || '#292524'}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* iter-386u-bo — Branding & UI Engine: 4 dynamic surfaces */}
              <div className="mt-8 pt-6 border-t border-stone-200" data-testid="branding-ui-engine">
                <h3 className="text-base font-semibold text-stone-900 mb-1">UI Surfaces</h3>
                <p className="text-xs text-stone-500 mb-4">
                  Change any of these and the whole platform re-skins instantly via CSS variables. Save to persist.
                </p>

                {/* iter-386u-bq — Live Preview Strip: mini mock-dashboard */}
                <div className="mb-5 rounded-xl border border-stone-200 overflow-hidden shadow-sm" data-testid="brand-live-preview">
                  <div className="bg-stone-100 px-3 py-1.5 text-[10px] uppercase tracking-wider text-stone-600 font-semibold flex items-center justify-between">
                    <span>Live Preview</span>
                    <span className="text-stone-400 normal-case tracking-normal">Updates as you pick colors</span>
                  </div>
                  <div className="flex" style={{ backgroundColor: settings.page_bg_color || '#f8fafc' }}>
                    {/* Mini sidebar */}
                    <div
                      className="w-24 py-3 px-2 flex flex-col gap-1.5 flex-shrink-0"
                      style={{
                        backgroundColor: settings.sidebar_color || '#0f172a',
                        color: (settings.sidebar_color || '#0f172a').toLowerCase() > '#888888' ? '#292524' : '#ffffff',
                      }}
                    >
                      <div className="h-1.5 rounded-full opacity-60" style={{ backgroundColor: 'currentColor', width: '70%' }} />
                      <div className="h-1.5 rounded-full opacity-40" style={{ backgroundColor: 'currentColor', width: '90%' }} />
                      <div className="h-1.5 rounded-full opacity-40" style={{ backgroundColor: 'currentColor', width: '55%' }} />
                      <div className="h-1.5 rounded-full opacity-40" style={{ backgroundColor: 'currentColor', width: '80%' }} />
                    </div>
                    {/* Mini main */}
                    <div className="flex-1 p-3 space-y-2">
                      <div className="flex gap-2">
                        {[0, 1, 2].map((i) => (
                          <div
                            key={i}
                            className="flex-1 h-14 rounded-lg border border-stone-200 p-2 shadow-sm"
                            style={{ backgroundColor: settings.card_bg_color || '#ffffff' }}
                          >
                            <div className="h-1.5 w-1/2 rounded-full bg-stone-200 mb-1.5" />
                            <div className="h-3 w-2/3 rounded font-bold" style={{ backgroundColor: settings.primary_color || '#0f172a', opacity: 0.85 }} />
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-md text-[11px] font-semibold shadow"
                          style={{
                            backgroundColor: settings.button_color || '#059669',
                            color: '#ffffff',
                          }}
                        >
                          Primary CTA
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* iter-386u-bp — One-click theme presets */}
                <div className="mb-5 p-3 rounded-lg bg-gradient-to-br from-stone-50 to-white border border-stone-200" data-testid="theme-presets">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-semibold text-stone-900">Start from a preset</div>
                      <div className="text-[10px] text-stone-500">One click applies all 7 colors below. Save to persist.</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    {[
                      {
                        key: 'slate_pro', label: 'Slate Pro', tone: 'Serious. Deep. Corporate.',
                        colors: {
                          sidebar_color: '#0f172a', page_bg_color: '#f1f5f9', card_bg_color: '#ffffff', button_color: '#0f766e',
                          primary_color: '#0f766e', secondary_color: '#475569', accent_color: '#0f172a',
                        },
                        swatch: ['#0f172a', '#0f766e', '#f1f5f9'],
                      },
                      {
                        key: 'sunlit', label: 'Sunlit', tone: 'Warm. Studio. Bright.',
                        colors: {
                          sidebar_color: '#fffbeb', page_bg_color: '#fefce8', card_bg_color: '#ffffff', button_color: '#d97706',
                          primary_color: '#d97706', secondary_color: '#a16207', accent_color: '#78350f',
                        },
                        swatch: ['#fffbeb', '#d97706', '#78350f'],
                      },
                      {
                        key: 'emerald_studio', label: 'Emerald Studio', tone: 'Modern. Fresh. AI.',
                        colors: {
                          sidebar_color: '#064e3b', page_bg_color: '#ecfdf5', card_bg_color: '#ffffff', button_color: '#059669',
                          primary_color: '#059669', secondary_color: '#065f46', accent_color: '#022c22',
                        },
                        swatch: ['#064e3b', '#059669', '#ecfdf5'],
                      },
                      // iter-386u-bu — Two new presets (extends 3 → 5 slots per Msg-919)
                      {
                        key: 'midnight_indigo', label: 'Midnight Indigo', tone: 'Bold. Dramatic. Tech.',
                        colors: {
                          sidebar_color: '#1e1b4b', page_bg_color: '#eef2ff', card_bg_color: '#ffffff', button_color: '#4f46e5',
                          primary_color: '#4f46e5', secondary_color: '#4338ca', accent_color: '#1e1b4b',
                        },
                        swatch: ['#1e1b4b', '#4f46e5', '#eef2ff'],
                      },
                      {
                        key: 'terracotta', label: 'Terracotta', tone: 'Earthy. Curated. Boutique.',
                        colors: {
                          sidebar_color: '#78350f', page_bg_color: '#fef3c7', card_bg_color: '#ffffff', button_color: '#c2410c',
                          primary_color: '#c2410c', secondary_color: '#9a3412', accent_color: '#431407',
                        },
                        swatch: ['#78350f', '#c2410c', '#fef3c7'],
                      },
                    ].map((preset) => (
                      <button
                        key={preset.key}
                        onClick={() => {
                          Object.entries(preset.colors).forEach(([k, v]) => handleChange(k, v));
                          toast.info(`${preset.label} palette loaded — click Save Changes to apply.`);
                        }}
                        className="text-left p-2.5 rounded-lg bg-white border border-stone-200 hover:border-emerald-400 hover:shadow-md transition group"
                        data-testid={`theme-preset-${preset.key}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="flex -space-x-1">
                            {preset.swatch.map((c, i) => (
                              <div key={i} className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: c }} />
                            ))}
                          </div>
                          <div className="text-sm font-bold text-stone-900 group-hover:text-emerald-700">{preset.label}</div>
                        </div>
                        <div className="text-[10px] text-stone-500 leading-tight">{preset.tone}</div>
                      </button>
                    ))}
                  </div>
                  <SavedThemePresets
                    settings={settings}
                    onApply={(colors) => Object.entries(colors).forEach(([k, v]) => handleChange(k, v))}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[
                    { field: 'sidebar_color', label: 'Navigation Sidebar', default: '#0f172a', testid: 'ui-color-sidebar' },
                    { field: 'page_bg_color', label: 'Page Background', default: '#f8fafc', testid: 'ui-color-page' },
                    { field: 'card_bg_color', label: 'Dashboard Cards', default: '#ffffff', testid: 'ui-color-card' },
                    { field: 'button_color', label: 'Action Buttons', default: '#059669', testid: 'ui-color-button' },
                  ].map(({ field, label, default: dflt, testid }) => (
                    <div key={field}>
                      <Label htmlFor={field} className="text-xs">{label}</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          id={field}
                          value={settings[field] || dflt}
                          onChange={(e) => handleChange(field, e.target.value)}
                          className="w-12 h-10 rounded border border-stone-200 cursor-pointer"
                          data-testid={testid}
                        />
                        <Input
                          value={settings[field] || dflt}
                          onChange={(e) => handleChange(field, e.target.value)}
                          className="flex-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="mt-8">
                <Label>Preview</Label>
                <div className="mt-2 p-6 border border-stone-200 rounded-lg bg-stone-50">
                  <div className="flex items-center gap-4 mb-4">
                    {settings.logo_url ? (
                      <img
                        src={getLogoUrl(settings.logo_url)}
                        alt="Logo"
                        className="w-16 h-16 object-contain"
                      />
                    ) : (
                      <div 
                        className="w-16 h-16 rounded flex items-center justify-center text-white font-bold text-xl"
                        style={{ backgroundColor: settings.primary_color }}
                      >
                        {settings.organization_name?.charAt(0) || 'B'}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-lg" style={{ color: settings.accent_color }}>
                        {settings.organization_name || 'Organization Name'}
                      </h3>
                      <p className="text-sm" style={{ color: settings.secondary_color }}>
                        {settings.industry || 'Interior Design'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      className="px-4 py-2 rounded text-white text-sm font-medium"
                      style={{ backgroundColor: settings.primary_color }}
                    >
                      Primary Button
                    </button>
                    <button 
                      className="px-4 py-2 rounded text-white text-sm font-medium"
                      style={{ backgroundColor: settings.secondary_color }}
                    >
                      Secondary Button
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            {/* Quick links to dedicated template pages */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a href="/settings/pdf-templates" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-stone-200 hover:border-amber-300 hover:shadow-sm transition-all group" data-testid="pdf-templates-link">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-stone-900">PDF Templates</p>
                  <p className="text-xs text-stone-500">Customize invoice, quotation & BOQ PDFs</p>
                </div>
              </a>
              <a href="/settings/email-templates" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-stone-200 hover:border-blue-300 hover:shadow-sm transition-all group" data-testid="email-templates-link">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-stone-900">Email Templates</p>
                  <p className="text-xs text-stone-500">Customize email notifications & templates</p>
                </div>
              </a>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                PDF Template Settings
              </h2>
              <p className="text-sm text-stone-500 mb-6">
                Configure the layout and appearance of your PDF documents. Changes are reflected in real-time preview.
              </p>
              
              {/* Live Template Preview */}
              <div className="border border-stone-200 rounded-lg p-6 bg-stone-50 mb-6">
                <p className="text-sm font-medium text-stone-700 mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Live Preview
                </p>
                
                {/* Logo + Details + Doc Type Row - NO DUPLICATE NAME AT TOP */}
                <div 
                  className="bg-white border border-stone-300 rounded shadow-sm overflow-hidden"
                >
                  {/* Single Row: Logo+Name | Address | Doc Type */}
                  <div 
                    className="flex items-start justify-between p-4"
                    style={{ 
                      paddingLeft: `${(settings.pdf_left_margin || 1) * 16}px`,
                      paddingRight: `${(settings.pdf_right_margin || 1) * 16}px`
                    }}
                  >
                    {/* Left: Logo + Name + Tagline */}
                    <div className="flex flex-col items-start gap-1" style={{ minWidth: '120px' }}>
                      {settings.logo_url ? (
                        <img
                          src={getLogoUrl(settings.logo_url)}
                          alt="Logo"
                          className="object-contain mb-1"
                          style={{ 
                            width: `${settings.pdf_logo_width || 70}px`, 
                            height: `${settings.pdf_logo_height || 45}px` 
                          }}
                        />
                      ) : (
                        <div 
                          className="bg-stone-200 rounded flex items-center justify-center text-stone-400 text-xs mb-1"
                          style={{ 
                            width: `${settings.pdf_logo_width || 70}px`, 
                            height: `${settings.pdf_logo_height || 45}px` 
                          }}
                        >
                          Logo
                        </div>
                      )}
                      <p 
                        className="font-bold text-stone-800"
                        style={{ fontSize: `${settings.pdf_company_name_size || 10}px` }}
                      >
                        {(settings.organization_name || 'COMPANY NAME').toUpperCase()}
                      </p>
                      <p 
                        className="text-stone-500"
                        style={{ fontSize: `${settings.pdf_tagline_size || 8}px` }}
                      >
                        {settings.tagline || 'Luxury Interior Design & Execution'}
                      </p>
                    </div>
                    
                    {/* Center: Address */}
                    <div 
                      className="text-stone-600 text-center flex-1 mx-4"
                      style={{ fontSize: `${settings.pdf_address_size || 8}px` }}
                    >
                      <p>{settings.address_line1 || 'Address Line 1'}</p>
                      <p>{settings.city || 'City'}, {settings.state || 'State'}</p>
                      <p>Ph: {settings.phone || '0000000000'}</p>
                      <p>GSTIN: {settings.gstin || 'XXXXXXXXX'}</p>
                    </div>
                    
                    {/* Right: Document Type */}
                    <div className="text-right" style={{ minWidth: '100px' }}>
                      <p 
                        className="font-bold text-stone-800"
                        style={{ fontSize: `${settings.pdf_doc_type_size || 12}px` }}
                      >
                        BILL OF<br/>QUANTITIES
                      </p>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-stone-500 mt-3 text-center">
                  This layout applies to all PDF exports: BOQ, Quotation, Invoice, and Purchase Orders
                </p>
              </div>
              
              {/* Font Size Settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <Label htmlFor="pdf_header_font_size">Header Title Size</Label>
                  <Select 
                    value={String(settings.pdf_header_font_size || 14)}
                    onValueChange={(value) => handleChange('pdf_header_font_size', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">12px (Small)</SelectItem>
                      <SelectItem value="14">14px (Default)</SelectItem>
                      <SelectItem value="16">16px (Medium)</SelectItem>
                      <SelectItem value="18">18px (Large)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="pdf_company_name_size">Company Name Size</Label>
                  <Select 
                    value={String(settings.pdf_company_name_size || 10)}
                    onValueChange={(value) => handleChange('pdf_company_name_size', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8">8px (Small)</SelectItem>
                      <SelectItem value="10">10px (Default)</SelectItem>
                      <SelectItem value="12">12px (Medium)</SelectItem>
                      <SelectItem value="14">14px (Large)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="pdf_doc_type_size">Document Type Size</Label>
                  <Select 
                    value={String(settings.pdf_doc_type_size || 12)}
                    onValueChange={(value) => handleChange('pdf_doc_type_size', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10px (Small)</SelectItem>
                      <SelectItem value="12">12px (Default)</SelectItem>
                      <SelectItem value="14">14px (Medium)</SelectItem>
                      <SelectItem value="16">16px (Large)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="pdf_address_size">Address Font Size</Label>
                  <Select 
                    value={String(settings.pdf_address_size || 8)}
                    onValueChange={(value) => handleChange('pdf_address_size', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7px (Small)</SelectItem>
                      <SelectItem value="8">8px (Default)</SelectItem>
                      <SelectItem value="9">9px (Medium)</SelectItem>
                      <SelectItem value="10">10px (Large)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="pdf_tagline_size">Tagline Size</Label>
                  <Select 
                    value={String(settings.pdf_tagline_size || 8)}
                    onValueChange={(value) => handleChange('pdf_tagline_size', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7px (Small)</SelectItem>
                      <SelectItem value="8">8px (Default)</SelectItem>
                      <SelectItem value="9">9px (Medium)</SelectItem>
                      <SelectItem value="10">10px (Large)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Tagline Text</Label>
                  <Input
                    value={settings.tagline || ''}
                    onChange={(e) => handleChange('tagline', e.target.value)}
                    placeholder="Luxury Interior Design & Execution"
                  />
                </div>
              </div>
              
              {/* Logo Size Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label htmlFor="pdf_logo_width">Logo Width (px)</Label>
                  <Select 
                    value={String(settings.pdf_logo_width || 70)}
                    onValueChange={(value) => handleChange('pdf_logo_width', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select width" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50px (Small)</SelectItem>
                      <SelectItem value="70">70px (Default)</SelectItem>
                      <SelectItem value="90">90px (Medium)</SelectItem>
                      <SelectItem value="110">110px (Large)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="pdf_logo_height">Logo Height (px)</Label>
                  <Select 
                    value={String(settings.pdf_logo_height || 45)}
                    onValueChange={(value) => handleChange('pdf_logo_height', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select height" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="35">35px (Small)</SelectItem>
                      <SelectItem value="45">45px (Default)</SelectItem>
                      <SelectItem value="55">55px (Medium)</SelectItem>
                      <SelectItem value="65">65px (Large)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Margin Settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <Label htmlFor="pdf_left_margin">Left Margin (inches)</Label>
                  <Select 
                    value={String(settings.pdf_left_margin || 1)}
                    onValueChange={(value) => handleChange('pdf_left_margin', parseFloat(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select margin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">0.5 in (Narrow)</SelectItem>
                      <SelectItem value="1">1.0 in (Default)</SelectItem>
                      <SelectItem value="1.5">1.5 in (Wide)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="pdf_center_margin">Center Spacing</Label>
                  <Select 
                    value={String(settings.pdf_center_margin || 1)}
                    onValueChange={(value) => handleChange('pdf_center_margin', parseFloat(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select spacing" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">Compact</SelectItem>
                      <SelectItem value="1">Default</SelectItem>
                      <SelectItem value="1.5">Spacious</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="pdf_right_margin">Right Margin (inches)</Label>
                  <Select 
                    value={String(settings.pdf_right_margin || 1)}
                    onValueChange={(value) => handleChange('pdf_right_margin', parseFloat(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select margin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">0.5 in (Narrow)</SelectItem>
                      <SelectItem value="1">1.0 in (Default)</SelectItem>
                      <SelectItem value="1.5">1.5 in (Wide)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Paper and Font Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="paper_size">Paper Size</Label>
                  <Select 
                    value={settings.pdf_paper_size || 'A4'}
                    onValueChange={(value) => handleChange('pdf_paper_size', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select paper size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4 (210 × 297 mm)</SelectItem>
                      <SelectItem value="Letter">Letter (8.5 × 11 in)</SelectItem>
                      <SelectItem value="Legal">Legal (8.5 × 14 in)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="pdf_font">Font Family</Label>
                  <Select 
                    value={settings.pdf_font || 'Helvetica'}
                    onValueChange={(value) => handleChange('pdf_font', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Helvetica">Helvetica (Default)</SelectItem>
                      <SelectItem value="Times">Times New Roman</SelectItem>
                      <SelectItem value="Courier">Courier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Show Estimated vs Actual Toggle */}
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-stone-800">Estimated vs Actual Columns</p>
                    <p className="text-sm text-stone-600">Show color-coded comparison columns in BOQ exports</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">Estimated (Blue)</span>
                    <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">Actual (Green)</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                Document Prefixes
              </h2>
              <p className="text-sm text-stone-500 mb-6">
                Customize the prefixes used for automatically generated document numbers.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="invoice_prefix">Invoice Prefix</Label>
                  <Input
                    id="invoice_prefix"
                    value={settings.invoice_prefix || 'INV'}
                    onChange={(e) => handleChange('invoice_prefix', e.target.value.toUpperCase())}
                    placeholder="INV"
                    maxLength={5}
                  />
                  <p className="text-xs text-stone-500 mt-1">Example: INV-2026-001</p>
                </div>
                <div>
                  <Label htmlFor="quotation_prefix">Quotation Prefix</Label>
                  <Input
                    id="quotation_prefix"
                    value={settings.quotation_prefix || 'QT'}
                    onChange={(e) => handleChange('quotation_prefix', e.target.value.toUpperCase())}
                    placeholder="QT"
                    maxLength={5}
                  />
                  <p className="text-xs text-stone-500 mt-1">Example: QT-2026-001</p>
                </div>
                <div>
                  <Label htmlFor="po_prefix">Purchase Order Prefix</Label>
                  <Input
                    id="po_prefix"
                    value={settings.po_prefix || 'PO'}
                    onChange={(e) => handleChange('po_prefix', e.target.value.toUpperCase())}
                    placeholder="PO"
                    maxLength={5}
                  />
                  <p className="text-xs text-stone-500 mt-1">Example: PO-2026-001</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4">Default Terms</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="payment_terms">Default Payment Terms</Label>
                  <Textarea
                    id="payment_terms"
                    value={settings.default_payment_terms || ''}
                    onChange={(e) => handleChange('default_payment_terms', e.target.value)}
                    placeholder="50% advance, 40% progressive, 10% on completion"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="warranty_terms">Default Warranty Terms</Label>
                  <Textarea
                    id="warranty_terms"
                    value={settings.default_warranty_terms || ''}
                    onChange={(e) => handleChange('default_warranty_terms', e.target.value)}
                    placeholder="1 year warranty on workmanship, 5 years on carpentry"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Bank Details Section */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-green-600" />
                Bank Details
              </h2>
              <p className="text-sm text-stone-500 mb-6">
                These details will appear on all Invoice and Quotation PDF exports.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bank_account_name">Account Holder Name</Label>
                  <Input
                    id="bank_account_name"
                    value={settings.bank_account_name || ''}
                    onChange={(e) => handleChange('bank_account_name', e.target.value)}
                    placeholder="e.g. Bella Casa Interiors"
                    data-testid="bank-account-name"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={settings.bank_name || ''}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    placeholder="e.g. HDFC Bank"
                    data-testid="bank-name"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_branch">Branch</Label>
                  <Input
                    id="bank_branch"
                    value={settings.bank_branch || ''}
                    onChange={(e) => handleChange('bank_branch', e.target.value)}
                    placeholder="e.g. Andheri West, Mumbai"
                    data-testid="bank-branch"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_account_no">Account Number</Label>
                  <Input
                    id="bank_account_no"
                    value={settings.bank_account_no || ''}
                    onChange={(e) => handleChange('bank_account_no', e.target.value)}
                    placeholder="e.g. 1234567890123"
                    data-testid="bank-account-no"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_ifsc">IFSC Code</Label>
                  <Input
                    id="bank_ifsc"
                    value={settings.bank_ifsc || ''}
                    onChange={(e) => handleChange('bank_ifsc', e.target.value.toUpperCase())}
                    placeholder="e.g. HDFC0001234"
                    data-testid="bank-ifsc"
                  />
                </div>
                <div>
                  <Label htmlFor="upi_id">UPI ID (Optional)</Label>
                  <Input
                    id="upi_id"
                    value={settings.upi_id || ''}
                    onChange={(e) => handleChange('upi_id', e.target.value)}
                    placeholder="e.g. company@upi"
                    data-testid="bank-upi"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Social Tab */}
          <TabsContent value="social" className="space-y-6">
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-medium text-stone-900 mb-4">Social Media Links</h2>
              <p className="text-sm text-stone-500 mb-6">
                Add your social media profiles. These will appear on exported documents.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="facebook">Facebook</Label>
                  <div className="relative">
                    <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-600" />
                    <Input
                      id="facebook"
                      value={settings.social_facebook || ''}
                      onChange={(e) => handleChange('social_facebook', e.target.value)}
                      placeholder="https://facebook.com/bellacasa"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-600" />
                    <Input
                      id="instagram"
                      value={settings.social_instagram || ''}
                      onChange={(e) => handleChange('social_instagram', e.target.value)}
                      placeholder="https://instagram.com/bellacasa"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn</Label>
                  <div className="relative">
                    <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-700" />
                    <Input
                      id="linkedin"
                      value={settings.social_linkedin || ''}
                      onChange={(e) => handleChange('social_linkedin', e.target.value)}
                      placeholder="https://linkedin.com/company/bellacasa"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="youtube">YouTube</Label>
                  <div className="relative">
                    <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-600" />
                    <Input
                      id="youtube"
                      value={settings.social_youtube || ''}
                      onChange={(e) => handleChange('social_youtube', e.target.value)}
                      placeholder="https://youtube.com/@bellacasa"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="twitter">Twitter / X</Label>
                  <div className="relative">
                    <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-800" />
                    <Input
                      id="twitter"
                      value={settings.social_twitter || ''}
                      onChange={(e) => handleChange('social_twitter', e.target.value)}
                      placeholder="https://twitter.com/bellacasa"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          {/* iter-315c: Automation tab — vendor auto-notify on Convert-to-Bill */}
          <TabsContent value="automation" className="space-y-6">
            <AutomationSettings />
          </TabsContent>
          {/* iter-338m: AI Credits — configurable per-role free credit allowance */}
          <TabsContent value="ai-credits" className="space-y-6">
            <AICreditsSettings />
          </TabsContent>
          {isOwnerRole && (
            <TabsContent value="email-domain" className="space-y-6">
              <EmailDomainCard />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}

// iter-315c: Automation Settings panel
function AutomationSettings() {
  const [enabled, setEnabled] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
  React.useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/admin/settings/vendor-auto-notify`);
        setEnabled(!!r.data.enabled);
      } catch (e) {
        // default true
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  const toggle = async () => {
    setSaving(true);
    try {
      const next = !enabled;
      await axios.put(`${API_BASE}/admin/settings/vendor-auto-notify`, { enabled: next });
      setEnabled(next);
      toast.success(`Auto-notify ${next ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6">
      <h2 className="text-lg font-medium text-stone-900 mb-2 flex items-center gap-2">
        Vendor Automation
      </h2>
      <p className="text-sm text-stone-500 mb-4">Controls automated notifications sent to vendors.</p>
      <div className="flex items-start gap-4 p-4 rounded-lg border border-stone-200 bg-stone-50/60">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-stone-800">Auto-notify vendor on "Convert to Running Bill"</h3>
          <p className="text-xs text-stone-500 mt-1 leading-relaxed">
            When enabled, clicking <strong>Convert to Running Bill</strong> automatically emails the vendor (using the email on file) AND drops a bell notification with the deeplink.
            If they don't open the link within 2 days, a follow-up bell notification is sent (no extra email).
            When disabled, admin keeps the dialog open and manually triggers <strong>Email to Vendor</strong>.
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={loading || saving}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${enabled ? 'bg-emerald-600' : 'bg-stone-300'}`}
          data-testid="auto-notify-toggle"
          aria-pressed={enabled}
        >
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>
    </div>
  );
}


// iter-338m: AI Credits — per-role free-credit configuration panel.
// Admin/Owner only. Owner/Admin/Staff always run with UNLIMITED usage (the
// backend bypasses the credit deduction); this UI controls only the per-role
// free allowance granted to vendors / clients / labour / project managers on
// first chat. Setting a role's allowance to 0 effectively requires the admin
// to grant credits before any usage.
function AICreditsSettings() {
  const [settings, setSettings] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchSettings = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/admin/ai-chat-settings`, { headers: authHeaders });
      setSettings(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load AI credit settings');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  React.useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const updateRole = (role, value) => {
    setSettings((prev) => prev ? {
      ...prev,
      free_credits_by_role: { ...(prev.free_credits_by_role || {}), [role]: value },
    } : prev);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/admin/ai-chat-settings`, {
        free_credits_by_role: settings.free_credits_by_role || {},
      }, { headers: authHeaders });
      toast.success('AI credit allowances updated');
      await fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card><CardContent className="py-6 text-center text-stone-500">Loading…</CardContent></Card>;
  if (!settings) return null;
  const rows = ['vendor', 'client', 'labour', 'project_manager'];
  return (
    <Card data-testid="ai-credits-settings">
      <CardHeader>
        <CardTitle className="text-lg">AI Assistant — Free Credit Allowance</CardTitle>
        <CardDescription>
          Owner / Admin / Staff have <strong>unlimited</strong> AI usage and are not deducted credits. This panel controls how many free credits are auto-granted to other roles on their first chat. Each message costs <strong>{settings.cost_per_message} credit</strong>. Once depleted, the user must request a top-up via &quot;Grant credits&quot; on the user profile.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {rows.map((role) => {
            const cur = (settings.free_credits_by_role || {})[role];
            const def = (settings.defaults || {})[role] || 0;
            return (
              <div key={role} className="grid grid-cols-[160px_1fr_auto] items-center gap-3 py-2 border-b border-stone-100 last:border-0">
                <div className="text-sm font-medium capitalize">{role.replace('_', ' ')}</div>
                <input
                  type="number"
                  min={0}
                  max={100000}
                  value={cur ?? def}
                  onChange={(e) => updateRole(role, parseInt(e.target.value, 10) || 0)}
                  className="h-9 w-32 border border-stone-200 rounded px-2 text-sm"
                  data-testid={`ai-credits-${role}-input`}
                />
                <span className="text-xs text-stone-500">credits (default {def})</span>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex items-center justify-between pt-3 border-t border-stone-200">
          <div className="text-xs text-stone-500">
            Unlimited roles: <strong>{(settings.unlimited_roles || []).join(', ')}</strong>
          </div>
          <Button onClick={save} disabled={saving} data-testid="ai-credits-save-btn">
            {saving ? 'Saving…' : 'Save Allowances'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// iter-386u-aq — Live Persona Preview card.
// Shows a sample AI task line so admins see what the AI vocabulary will
// change to BEFORE they save the Nature of Business dropdown.
function PersonaPreview({ natureName }) {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Normalise the display name to the backend's `nature_key` slug.
        const key = String(natureName || '')
          .toLowerCase()
          .replace(/-/g, ' ')
          .replace(/\//g, ' ')
          .trim();
        const map = {
          'interior design': 'interior_design',
          'landscaping': 'landscaping',
          'general construction': 'general_construction',
          'retail fit out': 'retail_fitout',
          'retail fitout': 'retail_fitout',
          'modular kitchen': 'modular_kitchen',
          'facility management': 'facility_management',
        };
        const natureKey = map[key] || 'interior_design';
        const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [personaRes, presetsRes] = await Promise.all([
          axios.get(`${API}/organization/business-persona`, { headers: authHeaders }),
          axios.get(`${API}/organization/category-presets?nature_key=${encodeURIComponent(natureKey)}`, { headers: authHeaders }),
        ]);
        // For persona sample, temporarily map to whatever the current tenant
        // has, but pull the *preset's* first category to build a realistic
        // sample task string.
        const cats = presetsRes.data?.categories || [];
        const first = cats[0] || { name: 'Sample Category', subs: ['Sample Task'] };
        const sample = `AI Task Creator would suggest tasks like: "${(first.subs || [])[0] || 'Sample Task'}" under the "${first.name}" section.`;
        if (!cancelled) setPreview({ persona: personaRes.data?.task_persona || '', sample });
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [natureName]);

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs" data-testid="persona-preview-card">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        <span className="uppercase tracking-wider text-[10px] font-semibold text-amber-800">Live persona preview</span>
      </div>
      {loading ? (
        <div className="text-stone-500">Loading preview…</div>
      ) : preview ? (
        <div className="space-y-1.5">
          <div className="text-stone-700 leading-relaxed">{preview.persona}</div>
          <div className="text-stone-600 italic border-l-2 border-amber-300 pl-2">{preview.sample}</div>
        </div>
      ) : (
        <div className="text-stone-500">Save the setting to see the AI adapt to your industry.</div>
      )}
    </div>
  );
}

